// PartA2Goals.js - Goals component for Part A2
import React from 'react';
import { Input, Checkbox, Row, Col } from 'antd';

const { TextArea } = Input;

const PartA2Goals = ({ formData, handleInputChange, handleArrayInputChange }) => {
    return (
        <div className="a4-page font-[poppins] bg-white p-8 mx-auto text-[#333333]" style={{ maxWidth: '100%', width: '100%' }}>

            {/* --- SCHOOL GOALS SECTION --- */}
            <div className="mb-8 bg-[#E5E7EB] p-6 rounded-lg relative">
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <h2 className="text-2xl font-bold text-[#F97316] bg-white px-4 py-1 rounded-full shadow-sm">Goals!</h2>
                </div>

                <h3 className="text-center font-bold text-lg mb-6 mt-2">My goal in school last year was...</h3>

                {/* Past Goal */}
                <div className="bg-white p-4 rounded-lg shadow-sm mb-4 border-l-4 border-gray-400">
                    <div className="mb-3">
                        <label className="block font-semibold text-sm text-gray-600 mb-1">Goal:</label>
                        <TextArea
                            value={formData.school_goal_last_year}
                            onChange={(e) => handleInputChange('school_goal_last_year', e.target.value)}
                            className="bg-gray-50 border-gray-200 text-sm"
                            autoSize={{ minRows: 2 }}
                            placeholder="Describe your school goal from last year..."
                        />
                    </div>
                    <div className="flex items-center gap-4">
                        <label className="font-semibold text-sm text-gray-600">Status:</label>
                        <Checkbox
                            checked={formData.school_goal_status === 'accomplished'}
                            onChange={(e) => handleInputChange('school_goal_status', e.target.checked ? 'accomplished' : '')}
                            className="text-sm"
                        >
                            Accomplished
                        </Checkbox>
                        <Checkbox
                            checked={formData.school_goal_status === 'still_working'}
                            onChange={(e) => handleInputChange('school_goal_status', e.target.checked ? 'still_working' : '')}
                            className="text-sm"
                        >
                            Still working
                        </Checkbox>
                    </div>
                </div>

                {/* Importance */}
                <div className="bg-[#DBEAFE] p-4 rounded-lg shadow-sm mb-6 border-l-4 border-blue-400">
                    <label className="block font-bold text-sm text-blue-800 mb-2">Why was the goal important to you?</label>
                    <TextArea
                        value={formData.school_goal_importance}
                        onChange={(e) => handleInputChange('school_goal_importance', e.target.value)}
                        className="bg-white border-blue-200 text-sm"
                        autoSize={{ minRows: 2 }}
                    />
                </div>

                {/* New Goal */}
                <div className="bg-[#DBEAFE] p-4 rounded-lg shadow-sm mb-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block font-bold text-sm text-blue-800 mb-2">ONE specific goal I would like to achieve in school by the end of this year:</label>
                            <TextArea
                                value={formData.school_goal_this_year}
                                onChange={(e) => handleInputChange('school_goal_this_year', e.target.value)}
                                className="bg-white border-blue-200 text-sm"
                                autoSize={{ minRows: 3 }}
                            />
                        </div>
                        <div>
                            <label className="block font-bold text-sm text-blue-800 mb-2">How will I know I have achieved this goal?</label>
                            <TextArea
                                value={formData.school_goal_achievement_indicator}
                                onChange={(e) => handleInputChange('school_goal_achievement_indicator', e.target.value)}
                                className="bg-white border-blue-200 text-sm"
                                autoSize={{ minRows: 3 }}
                            />
                        </div>
                    </div>
                </div>

                {/* Action Steps */}
                <div className="bg-[#DBEAFE] p-4 rounded-lg shadow-sm mb-4">
                    <label className="block font-bold text-sm text-blue-800 mb-3">To achieve this goal, things I need to do:</label>
                    <div className="grid grid-cols-3 gap-3">
                        <div className="bg-white p-3 rounded border border-blue-200">
                            <h4 className="font-semibold text-xs text-blue-600 mb-2 uppercase">A week from now</h4>
                            <TextArea
                                value={formData.school_goal_week_action}
                                onChange={(e) => handleInputChange('school_goal_week_action', e.target.value)}
                                className="border-0 bg-gray-50 text-xs p-2 min-h-[60px]"
                                placeholder="Action steps..."
                            />
                        </div>
                        <div className="bg-white p-3 rounded border border-blue-200">
                            <h4 className="font-semibold text-xs text-blue-600 mb-2 uppercase">6 weeks from now</h4>
                            <TextArea
                                value={formData.school_goal_six_weeks_action}
                                onChange={(e) => handleInputChange('school_goal_six_weeks_action', e.target.value)}
                                className="border-0 bg-gray-50 text-xs p-2 min-h-[60px]"
                                placeholder="Action steps..."
                            />
                        </div>
                        <div className="bg-white p-3 rounded border border-blue-200">
                            <h4 className="font-semibold text-xs text-blue-600 mb-2 uppercase">6 months from now</h4>
                            <TextArea
                                value={formData.school_goal_six_months_action}
                                onChange={(e) => handleInputChange('school_goal_six_months_action', e.target.value)}
                                className="border-0 bg-gray-50 text-xs p-2 min-h-[60px]"
                                placeholder="Action steps..."
                            />
                        </div>
                    </div>
                </div>

                {/* Support System */}
                <div className="bg-[#DBEAFE] rounded-lg shadow-sm overflow-hidden border border-blue-200">
                    <div className="bg-blue-100 p-2 text-center font-bold text-sm text-blue-800 border-b border-blue-200">
                        Things that will help me achieve this goal
                    </div>
                    <div className="grid grid-cols-3 divide-x divide-blue-200">
                        <div className="p-3">
                            <h4 className="font-bold text-xs text-center mb-2">My strengths/abilities</h4>
                            {(formData.school_goal_strengths || ['', '', '']).map((item, i) => (
                                <div key={i} className="flex items-center mb-1">
                                    <span className="text-xs font-bold mr-1">{i + 1}.</span>
                                    <Input
                                        value={item}
                                        onChange={(e) => handleArrayInputChange('school_goal_strengths', i, e.target.value)}
                                        className="text-xs border-b border-gray-300 rounded-none px-1 py-0 bg-transparent focus:shadow-none"
                                        bordered={false}
                                    />
                                </div>
                            ))}
                        </div>
                        <div className="p-3">
                            <h4 className="font-bold text-xs text-center mb-2">Support I get at home/community</h4>
                            {(formData.school_goal_home_support || ['', '', '']).map((item, i) => (
                                <div key={i} className="flex items-center mb-1">
                                    <span className="text-xs font-bold mr-1">{i + 1}.</span>
                                    <Input
                                        value={item}
                                        onChange={(e) => handleArrayInputChange('school_goal_home_support', i, e.target.value)}
                                        className="text-xs border-b border-gray-300 rounded-none px-1 py-0 bg-transparent focus:shadow-none"
                                        bordered={false}
                                    />
                                </div>
                            ))}
                        </div>
                        <div className="p-3">
                            <h4 className="font-bold text-xs text-center mb-2">Support I get at school</h4>
                            {(formData.school_goal_school_support || ['', '', '']).map((item, i) => (
                                <div key={i} className="flex items-center mb-1">
                                    <span className="text-xs font-bold mr-1">{i + 1}.</span>
                                    <Input
                                        value={item}
                                        onChange={(e) => handleArrayInputChange('school_goal_school_support', i, e.target.value)}
                                        className="text-xs border-b border-gray-300 rounded-none px-1 py-0 bg-transparent focus:shadow-none"
                                        bordered={false}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* --- OUTSIDE SCHOOL GOALS SECTION --- */}
            <div className="mb-8 bg-[#DBEAFE]/30 p-6 rounded-lg relative border-t-4 border-[#F97316]">
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <h2 className="text-xl font-bold text-[#F97316] bg-white px-4 py-1 rounded-full shadow-sm border border-orange-100">Goals!</h2>
                </div>

                <h3 className="text-center font-bold text-lg mb-6 mt-2">My goal outside of school last year was...</h3>

                {/* Past Goal */}
                <div className="bg-white p-4 rounded-lg shadow-sm mb-4 border-l-4 border-gray-400">
                    <div className="mb-3">
                        <label className="block font-semibold text-sm text-gray-600 mb-1">Goal:</label>
                        <TextArea
                            value={formData.outside_goal_last_year}
                            onChange={(e) => handleInputChange('outside_goal_last_year', e.target.value)}
                            className="bg-gray-50 border-gray-200 text-sm"
                            autoSize={{ minRows: 2 }}
                            placeholder="Describe your outside school goal from last year..."
                        />
                    </div>
                    <div className="flex items-center gap-4">
                        <label className="font-semibold text-sm text-gray-600">Status:</label>
                        <Checkbox
                            checked={formData.outside_goal_status === 'accomplished'}
                            onChange={(e) => handleInputChange('outside_goal_status', e.target.checked ? 'accomplished' : '')}
                            className="text-sm"
                        >
                            Accomplished
                        </Checkbox>
                        <Checkbox
                            checked={formData.outside_goal_status === 'still_working'}
                            onChange={(e) => handleInputChange('outside_goal_status', e.target.checked ? 'still_working' : '')}
                            className="text-sm"
                        >
                            Still working
                        </Checkbox>
                    </div>
                </div>

                {/* New Goal */}
                <div className="bg-[#DBEAFE] p-4 rounded-lg shadow-sm mb-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block font-bold text-sm text-blue-800 mb-2">ONE specific goal I would like to achieve outside of school by the end of this year:</label>
                            <TextArea
                                value={formData.outside_goal_this_year}
                                onChange={(e) => handleInputChange('outside_goal_this_year', e.target.value)}
                                className="bg-white border-blue-200 text-sm"
                                autoSize={{ minRows: 3 }}
                            />
                        </div>
                        <div>
                            <label className="block font-bold text-sm text-blue-800 mb-2">How will I know I have achieved this goal?</label>
                            <TextArea
                                value={formData.outside_goal_achievement_indicator}
                                onChange={(e) => handleInputChange('outside_goal_achievement_indicator', e.target.value)}
                                className="bg-white border-blue-200 text-sm"
                                autoSize={{ minRows: 3 }}
                            />
                        </div>
                    </div>
                </div>

                {/* Action Steps */}
                <div className="bg-[#DBEAFE] p-4 rounded-lg shadow-sm mb-4">
                    <label className="block font-bold text-sm text-blue-800 mb-3">To achieve this goal, things I need to do:</label>
                    <div className="grid grid-cols-3 gap-3">
                        <div className="bg-white p-3 rounded border border-blue-200">
                            <h4 className="font-semibold text-xs text-blue-600 mb-2 uppercase">A week from now</h4>
                            <TextArea
                                value={formData.outside_goal_week_action}
                                onChange={(e) => handleInputChange('outside_goal_week_action', e.target.value)}
                                className="border-0 bg-gray-50 text-xs p-2 min-h-[60px]"
                                placeholder="Action steps..."
                            />
                        </div>
                        <div className="bg-white p-3 rounded border border-blue-200">
                            <h4 className="font-semibold text-xs text-blue-600 mb-2 uppercase">6 weeks from now</h4>
                            <TextArea
                                value={formData.outside_goal_six_weeks_action}
                                onChange={(e) => handleInputChange('outside_goal_six_weeks_action', e.target.value)}
                                className="border-0 bg-gray-50 text-xs p-2 min-h-[60px]"
                                placeholder="Action steps..."
                            />
                        </div>
                        <div className="bg-white p-3 rounded border border-blue-200">
                            <h4 className="font-semibold text-xs text-blue-600 mb-2 uppercase">6 months from now</h4>
                            <TextArea
                                value={formData.outside_goal_six_months_action}
                                onChange={(e) => handleInputChange('outside_goal_six_months_action', e.target.value)}
                                className="border-0 bg-gray-50 text-xs p-2 min-h-[60px]"
                                placeholder="Action steps..."
                            />
                        </div>
                    </div>
                </div>

                {/* Support System */}
                <div className="bg-[#DBEAFE] rounded-lg shadow-sm overflow-hidden border border-blue-200 mb-6">
                    <div className="bg-blue-100 p-2 text-center font-bold text-sm text-blue-800 border-b border-blue-200">
                        Things that will help me achieve this goal
                    </div>
                    <div className="grid grid-cols-3 divide-x divide-blue-200">
                        <div className="p-3">
                            <h4 className="font-bold text-xs text-center mb-2">My strengths/abilities</h4>
                            {(formData.outside_goal_strengths || ['', '', '']).map((item, i) => (
                                <div key={i} className="flex items-center mb-1">
                                    <span className="text-xs font-bold mr-1">{i + 1}.</span>
                                    <Input
                                        value={item}
                                        onChange={(e) => handleArrayInputChange('outside_goal_strengths', i, e.target.value)}
                                        className="text-xs border-b border-gray-300 rounded-none px-1 py-0 bg-transparent focus:shadow-none"
                                        bordered={false}
                                    />
                                </div>
                            ))}
                        </div>
                        <div className="p-3">
                            <h4 className="font-bold text-xs text-center mb-2">Support I get at home/community</h4>
                            {(formData.outside_goal_home_support || ['', '', '']).map((item, i) => (
                                <div key={i} className="flex items-center mb-1">
                                    <span className="text-xs font-bold mr-1">{i + 1}.</span>
                                    <Input
                                        value={item}
                                        onChange={(e) => handleArrayInputChange('outside_goal_home_support', i, e.target.value)}
                                        className="text-xs border-b border-gray-300 rounded-none px-1 py-0 bg-transparent focus:shadow-none"
                                        bordered={false}
                                    />
                                </div>
                            ))}
                        </div>
                        <div className="p-3">
                            <h4 className="font-bold text-xs text-center mb-2">Support I get at school</h4>
                            {(formData.outside_goal_school_support || ['', '', '']).map((item, i) => (
                                <div key={i} className="flex items-center mb-1">
                                    <span className="text-xs font-bold mr-1">{i + 1}.</span>
                                    <Input
                                        value={item}
                                        onChange={(e) => handleArrayInputChange('outside_goal_school_support', i, e.target.value)}
                                        className="text-xs border-b border-gray-300 rounded-none px-1 py-0 bg-transparent focus:shadow-none"
                                        bordered={false}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Importance */}
                <div className="bg-[#DBEAFE] p-4 rounded-lg shadow-sm mb-6 border-l-4 border-blue-400">
                    <label className="block font-bold text-sm text-blue-800 mb-2">Why does this goal matter to you?</label>
                    <TextArea
                        value={formData.outside_goal_importance}
                        onChange={(e) => handleInputChange('outside_goal_importance', e.target.value)}
                        className="bg-white border-blue-200 text-sm"
                        autoSize={{ minRows: 2 }}
                    />
                </div>

                {/* Achievements */}
                <div className="bg-[#FEF3C7] p-4 rounded-lg shadow-sm border-l-4 border-yellow-400">
                    <label className="block font-bold text-sm text-yellow-800 mb-2">Some goals I have achieved till date and am proud of/happy with...</label>
                    <TextArea
                        value={formData.achieved_goals_proud_of}
                        onChange={(e) => handleInputChange('achieved_goals_proud_of', e.target.value)}
                        className="bg-white border-yellow-200 text-sm"
                        autoSize={{ minRows: 3 }}
                    />
                </div>
            </div>
        </div>
    );
};

export default PartA2Goals;